// contactUs — validation for the footer "Contact us" enquiry.
//
// Pure. No React, no fetching: the form renders, this decides what is valid.
//
// The bounds are NOT restated here — `ContactUsRequestSchema` is the arbiter,
// shared with the BFF and the Accounts route, so the three can never disagree
// about what a valid enquiry is.

import { ContactUsRequestSchema, type ContactUsRequest } from '@rheora/enrollment-contract';

/** Field-keyed messages; empty object means the form may be submitted. */
export type ContactUsErrors = Partial<Record<keyof ContactUsRequest, string>>;

export interface ContactUsDraft {
  readonly name: string;
  readonly email: string;
  readonly message: string;
}

export const EMPTY_CONTACT_US: ContactUsDraft = {
  name: '',
  email: '',
  message: '',
};

/** The payload for `POST /bff/contact-us`, built from what was typed. */
export function toContactUsRequest(draft: ContactUsDraft): ContactUsRequest {
  return {
    name: draft.name.trim(),
    email: draft.email.trim(),
    message: draft.message.trim(),
  };
}

/**
 * Validate a draft, returning one message per bad field.
 *
 * Messages are written for the person filling the form (same convention as
 * `validateLeadContact`): they say what to DO, not which rule fired.
 */
export function validateContactUs(draft: ContactUsDraft): ContactUsErrors {
  const errors: ContactUsErrors = {};
  const candidate = toContactUsRequest(draft);

  const parsed = ContactUsRequestSchema.safeParse(candidate);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if (field === 'name' && errors.name === undefined) {
        errors.name = 'Enter your name.';
      }
      if (field === 'email' && errors.email === undefined) {
        errors.email = 'Enter a valid email address.';
      }
      if (field === 'message' && errors.message === undefined) {
        errors.message =
          candidate.message === ''
            ? 'Enter a message.'
            : candidate.message.length < 5
              ? 'Tell us a little more — a few words at least.'
              : 'Shorten your message to 2000 characters.';
      }
    }
  }
  return errors;
}
